import type { HourlyPeriod } from "@types"
import type { Source } from "@types"
import { isOptimTurpeTemplateC2 } from "@modules/courtage/constants/optim-turpe"
import type {
    OptimTurpeAnalyse,
    OptimTurpeResultDetailProps,
    OptimTurpeTimelinePlageKwh,
} from "@modules/courtage/types/optim-turpe"

const PLAGE_ORDER = ["hcb", "hpb", "hch", "hph"] as const

/**
 * Convertit une heure API (`HH:MM:SS` ou `HH:MM`) au format français **HHhMM** (ex. 22h30).
 */
function formatTimeHhMm(time: string | undefined | null): string {
    if (!time || typeof time !== "string") return ""
    const parts = time.trim().split(":")
    const h = Number.parseInt(parts[0] ?? "0", 10)
    const m = Number.parseInt(parts[1] ?? "0", 10)
    if (!Number.isFinite(h) || !Number.isFinite(m)) return time.trim()
    const hh = String(Math.min(23, Math.max(0, h))).padStart(2, "0")
    const mm = String(Math.min(59, Math.max(0, m))).padStart(2, "0")
    return `${hh}h${mm}`
}

/** Formate les plages HC : créneaux en HHhMM, séparés par des tirets. */
export function formatHeuresCreusesDetail(
    periods: HourlyPeriod[] | null | undefined
): string {
    if (!periods?.length) return "-"
    return periods
        .map(
            (p) => `${formatTimeHhMm(p.time_from)}–${formatTimeHhMm(p.time_to)}`
        )
        .join(", ")
}

function getHcPeriodsFromMeter(meter?: Source | null): HourlyPeriod[] | null {
    return (
        meter?.informations?.pdl?.comptage?.plages_horaires?.heures_creuses ??
        null
    )
}

/**
 * Props pour `OptimTurpeResultDetail` (timeline en kWh numériques + champs texte).
 */
export function buildOptimTurpeResultDetailProps(
    analyse: OptimTurpeAnalyse,
    meter?: Source | null
): OptimTurpeResultDetailProps {
    const data = analyse.data
    const segment = String(data?.segment?.value ?? "")
    const isTemplateC2 = isOptimTurpeTemplateC2(segment)
    const isStrictC5 = segment === "C5"

    const classes: string[] = [...PLAGE_ORDER]
    if (isTemplateC2) classes.push("pte")

    let hasAnyEnergy = false
    const energies: Record<string, number> = {}
    for (const c of classes) {
        const apiKey = `E_${c.toUpperCase()}`
        const raw = data?.[apiKey]?.value
        if (typeof raw === "number") {
            energies[c] = raw
            hasAnyEnergy = true
        }
    }

    let totalEnergy = 0
    if (hasAnyEnergy) {
        totalEnergy = classes.reduce(
            (acc, c) =>
                acc + (typeof energies[c] === "number" ? energies[c] : 0),
            0
        )
    }

    const timelinePlages: OptimTurpeTimelinePlageKwh[] = classes.map(
        (plageKey) => ({
            plageKey,
            kwh: Math.round(
                typeof energies[plageKey] === "number" ? energies[plageKey]! : 0
            ),
        })
    )

    const totalConsumptionKwh = hasAnyEnergy
        ? Math.round(totalEnergy)
        : undefined

    const prMet = data?.puissance_raccordement
    const puissanceRaw = prMet?.value ?? prMet?.str_value
    const connectionPowerDisplay =
        puissanceRaw !== undefined &&
        puissanceRaw !== null &&
        String(puissanceRaw).trim() !== ""
            ? `${String(puissanceRaw)} kVA`
            : "-"

    const dfMet = data?.date_from_periode_analyse
    const dtMet = data?.date_to_periode_analyse
    const dateFrom = dfMet?.value ?? dfMet?.str_value
    const dateTo = dtMet?.value ?? dtMet?.str_value

    const periodDateFrom =
        dateFrom != null && String(dateFrom).trim() !== ""
            ? String(dateFrom)
            : undefined
    const periodDateTo =
        dateTo != null && String(dateTo).trim() !== ""
            ? String(dateTo)
            : undefined

    const hcPeriods = getHcPeriodsFromMeter(meter)
    const hcDisplay = formatHeuresCreusesDetail(hcPeriods)
    const hcCount = hcPeriods?.length ?? 0

    const tri = data?.triphase?.value

    const base: OptimTurpeResultDetailProps = {
        segment,
        timelinePlages,
        totalConsumptionKwh,
        connectionPowerDisplay,
        periodDateFrom,
        periodDateTo,
    }

    if (isStrictC5) {
        return {
            ...base,
            connectionTriphase: Boolean(tri),
            heuresCreusesDisplay: hcDisplay,
            heuresCreusesPeriodCount: hcCount,
        }
    }

    return base
}
